import { CloseIcon, Logo } from '@mote/assets/icons'
import { useMediaQuery } from '@mote/helpers'
import { isMobile } from '@mote/helpers/browser'
import { EXTENSION_INSTALLED_STORAGE_KEY } from '@mote/helpers/constants'
import { useGoalsOnboarding } from '@mote/helpers/hooks/business'
import { reallyTruthy } from '@mote/helpers/validation'
import { Button, Spinner } from '@mote/ui'
import React from 'react'
import { matchPath, useHistory, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useAuth } from '../../auth/useAuth'
import { Content, Sidebar } from '../shared'
import { DashboardFooter } from './dashboardFooter'
import { DashboardHeader } from './dashboardHeader'

export const DashboardLayout = ({ children }) => {
  const history = useHistory()
  const { user } = useAuth()
  const goalsState = useGoalsOnboarding()
  const mediaBreakPoint = useMediaQuery([640, 920, 1200])
  const isMobileViewport = React.useMemo(
    () => mediaBreakPoint <= 920,
    [mediaBreakPoint]
  )
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(!isMobileViewport)
  const toggleSidebar = React.useCallback(() => {
    setIsSidebarOpen(prev => !prev)
  }, [])

  const NAV_LINKS = React.useMemo(() => {
    let productOnboardingHomePage = null

    if (
      !goalsState.userOnboardingRequired &&
      goalsState.productOnboardingRequired &&
      !(user?.isInstitutionalAdmin && !user?.isPaid)
    ) {
      const extensionInstalled = reallyTruthy(
        window.localStorage.getItem(EXTENSION_INSTALLED_STORAGE_KEY)
      )
      const chromeExtensionTask = extensionInstalled || isMobile()

      const tasks = [
        chromeExtensionTask,
        !goalsState.makeFirstMoteRequired
      ].filter(completed => !completed)

      if (!isMobile()) {
        productOnboardingHomePage = {
          text: 'Getting Started',
          path: '/',
          notifications: tasks.length
        }
      }
    }

    return [
      productOnboardingHomePage,
      {
        text: 'My Activity',
        path: '/account/activity',
        subPaths: ['/account/activity/:pageIndex']
      },
      {
        text: 'Account Settings',
        path: '/account',
        subPaths: [
          '/account/profile',
          '/account/notifications',
          '/account/users',
          '/account/referrals'
        ]
      },
      {
        text: 'Help & Support',
        path: '/help-support'
      },
      {
        text: 'Pricing',
        path: '/pricing'
      }
    ].filter(Boolean)
  }, [user, goalsState])

  const navLinks = React.useMemo(
    () =>
      NAV_LINKS.map((item, index) => {
        const isActive = Boolean(
          matchPath(location.pathname, {
            exact: true,
            path: [item.path, ...(item.subPaths ?? [])]
          })
        )

        return (
          <NavLink
            key={`${item.path}-${index}`}
            onClick={!isActive ? () => history.push(item.path) : null}
            isActive={isActive}
          >
            {item.text}
            {item.notifications > 0 && (
              <NotificationsBadge>{item.notifications}</NotificationsBadge>
            )}
            {item.notifications === null && (
              <NotificationsBadge>
                <Spinner radius={12} />
              </NotificationsBadge>
            )}
          </NavLink>
        )
      }),
    [NAV_LINKS]
  )

  React.useEffect(() => {
    if (user) return

    history.push('/')
  }, [user, history])

  React.useEffect(() => {
    setIsSidebarOpen(!isMobileViewport)

    if (location.pathname !== '/' || !isMobile()) return
    history.push('/account')
  }, [isMobileViewport])

  return (
    <>
      <Root>
        <Sidebar isOpen={isSidebarOpen} onToggle={toggleSidebar}>
          <Sidebar.Header
            layout='horizontal'
            align='center'
            justify='space-between'
            spreadX
          >
            <LogoContainer onClick={_ => history.push('/')}>
              <Logo />
            </LogoContainer>
            {isMobileViewport && (
              <CloseButton
                aria-label='Close sidebar'
                icon={<CloseIcon color='currentColor' />}
                onClick={toggleSidebar}
                variant='ghost'
              />
            )}
          </Sidebar.Header>
          <Sidebar.Content layout='vertical' spreadX spreadY>
            {navLinks}
          </Sidebar.Content>
        </Sidebar>
        <Container isSidebarOpen={isSidebarOpen}>
          <StyledHeader toggleSidebar={toggleSidebar} />
          <StyledContent variant='wide'>{children}</StyledContent>
          <DashboardFooter />
        </Container>
      </Root>
    </>
  )
}

const Root = styled.div`
  display: flex;
  width: 100%;
  min-height: 100%;
`

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 100%;
  width: 100%;
  min-height: 100%;
  padding: 0 40px;
  transition: margin 150ms ease-in-out;

  ${({ isSidebarOpen }) =>
    isSidebarOpen &&
    css`
      margin-left: 256px;

      @media (max-width: 920px) {
        margin: 0;
      }
    `};

  @media (max-width: 920px) {
    padding: 0 16px;
  }
`

const StyledHeader = styled(DashboardHeader)``

const StyledContent = styled(Content)`
  display: flex;
  flex-direction: column;
  padding: 0 16px !important;
`

const LogoContainer = styled.div`
  cursor: pointer;
`

const NavLink = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  color: #25252d;
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  height: 40px;
  padding: 0 16px;
  transition: all 150ms ease-in;
  background: transparent;
  border-radius: 4px;

  &:hover {
    color: #25252d;
    background: #ececf3;
  }

  &:active {
    color: #ac0ae8;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      color: #ac0ae8;
      background: #ececf3;
      pointer-events: none;
    `};
`

const NotificationsBadge = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  padding: 4px;
  width: 24px;
  height: 24px;
  border: 1.5px solid #d1d1e0;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 100%;
  color: #ac0ae8;
`

const CloseButton = styled(Button)`
  padding: 4px;
  height: 40px;
  width: 40px;

  svg {
    width: 100%;
    height: 100%;
  }
`
